import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollToPlugin from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

export default class Nav {
    constructor() {

        this.init();
        this.startX = 0;
        this.startY = 0;

    }

    init() {

        this.navHeight();

        this.initShowHideNav();

        this.initNavSearch();
        this.initNavCart();
        this.initNavBurger();
        this.initNavClose();

        this.initResponsiveSubMenus();

        this.initSwipeListeners();

        this.attachAnchorListeners();
        this.scrollToAnchorOnLoad();
    }

    // === NAVHEIGHT ===
    navHeight() {
        const navElement = document.querySelector('.nav-sticky');
        if (navElement) {
            const navHeight = navElement.offsetHeight;
            document.documentElement.style.setProperty('--nav-height', `${navHeight}px`);
        }
    }

    // === SHOW/HIDE NAV ===
    initShowHideNav() {
        const navSticky = document.querySelector('.nav-sticky');
        const logoImage = document.querySelector('.logo-image');
        const mq = window.matchMedia("(min-width: 1200px)");

        if (navSticky && logoImage) {
            const originalSrc = logoImage.getAttribute('src');
            const scrolledSrc = logoImage.getAttribute('data-src-scrolled');
            let scrollTriggerInstance;

            const updateNavBehavior = () => {
                const isDesktop = mq.matches;
                const logoHeight = isDesktop ? "126px" : "60px";

                gsap.set(logoImage, { height: logoHeight });

                let previousScrollY = window.scrollY;

                if (scrollTriggerInstance) {
                    scrollTriggerInstance.kill();
                }

                scrollTriggerInstance = ScrollTrigger.create({
                    start: "top top",
                    end: "bottom bottom",
                    onUpdate: () => {
                        const currentScrollY = window.scrollY;
                        const isScrollingDown = currentScrollY > previousScrollY;

                        gsap.to(navSticky, {
                            y: isScrollingDown ? "-100%" : "0%",
                            duration: 0.3,
                            ease: "power2.out",
                            onStart: () => {
                                if (!isScrollingDown && currentScrollY === 0) {
                                    navSticky.classList.remove('has-scrolled');
                                    logoImage.setAttribute('src', originalSrc);
                                    gsap.to(logoImage, { height: logoHeight, duration: 0.3, ease: "power1.out" });
                                }
                            },
                            onComplete: () => {
                                if (isScrollingDown) {
                                    const hasScrolledClass = navSticky.classList.contains('has-scrolled');
                                    if (!hasScrolledClass) {
                                        navSticky.classList.add('has-scrolled');
                                        logoImage.setAttribute('src', scrolledSrc);
                                        gsap.to(logoImage, { height: isDesktop ? "90px" : "60px", duration: 0.3, ease: "power1.out" });
                                    }
                                }
                            },
                        });

                        previousScrollY = currentScrollY;
                    },
                });
            };

            const handleResize = () => {
                updateNavBehavior();
            };

            updateNavBehavior();
            mq.addEventListener("change", updateNavBehavior);
            window.addEventListener("resize", handleResize);

            window.addEventListener("beforeunload", () => {
                if (scrollTriggerInstance) scrollTriggerInstance.kill();
                mq.removeEventListener("change", updateNavBehavior);
                window.removeEventListener("resize", handleResize);
            });
        }
    }


    // === NAV SEARCH ===
    initNavSearch() {
        const navSearchElements = document.querySelectorAll(".nav-search");
        if (!navSearchElements.length) return;

        navSearchElements.forEach(navSearch => {
            const searchInput = navSearch.querySelector(".search-box_input");

            if (searchInput) {
                navSearch.addEventListener("click", (e) => {
                    e.stopPropagation();
                    navSearch.classList.toggle("active");
                    searchInput.focus();
                });
            }
        });

        document.addEventListener("click", (e) => {
            navSearchElements.forEach(navSearch => {
                if (!navSearch.contains(e.target)) {
                    navSearch.classList.remove("active");
                }
            });
        });
    }


    // === NAV CART ===
    initNavCart() {
        document.querySelectorAll(".nav-cart").forEach(item => {
            item.addEventListener("click", () => this.toggleCart());
        });
        document.querySelectorAll(".nav-cart-modal .cart-close, .nav-cart-overlay").forEach(item => {
            item.addEventListener("click", () => this.toggleCart());
        });
    }

    // === TOGGLE NAV CART / NAV CART OVERLAY / NAV RESPONSIVE ===
    toggleCart() {
        const navCartModal = document.querySelector(".nav-cart-modal");
        const navCartOverlay = document.querySelector(".nav-cart-overlay");
        const navResponsive = document.querySelector(".nav-responsive");
        const navBurger = document.querySelector(".nav-burger");
        const navOverlay = document.querySelector(".nav-responsive-overlay");
        const navSticky = document.querySelector(".nav-sticky");

        const isCartModalActive = navCartModal.classList.contains("active");

        navCartModal.classList.toggle("active");
        navCartOverlay.classList.toggle("active");

        if (!isCartModalActive) {
            navResponsive.classList.remove("active");
            navBurger.classList.remove("active");
            navOverlay.classList.remove("active");
            navSticky.classList.remove("nav-fixed");

            document.documentElement.classList.add("block-viewport");
            document.body.classList.add("block-viewport");
        } else {
            document.documentElement.classList.remove("block-viewport");
            document.body.classList.remove("block-viewport");
        }
    }


    // === NAV BURGER ===
    initNavBurger() {
        const navBurger = document.querySelector(".nav-burger");
        const navResponsive = document.querySelector(".nav-responsive");
        const navOverlay = document.querySelector(".nav-responsive-overlay");
        const navSticky = document.querySelector(".nav-sticky");
        const closeIcon = document.querySelector(".nav-responsive-close_icon");

        if (navBurger && navResponsive && navOverlay && navSticky && closeIcon) {
            [navBurger, navOverlay].forEach(item => {
                item.addEventListener("click", () => {
                    const isActive = navBurger.classList.toggle("active");
                    navResponsive.classList.toggle("active");
                    navOverlay.classList.toggle("active");
                    document.documentElement.classList.toggle("block-viewport");
                    document.body.classList.toggle("block-viewport");

                    if (isActive) {
                        navSticky.classList.add("nav-fixed");
                        closeIcon.classList.add("active");
                    } else {
                        navSticky.classList.remove("nav-fixed");
                        closeIcon.classList.remove("active");
                    }
                });
            });
        }
    }

    // === NAV CLOSE ===
    initNavClose() {
        const closeIcon = document.querySelector('.nav-responsive-close_icon');
        const navResponsive = document.querySelector('.nav-responsive');
        const navBurger = document.querySelector('.nav-burger');
        const navOverlay = document.querySelector('.nav-responsive-overlay');
        const navSticky = document.querySelector('.nav-sticky');

        if (closeIcon && navResponsive && navBurger && navOverlay && navSticky) {
            closeIcon.addEventListener('click', () => {
                navResponsive.classList.remove('active');
                navBurger.classList.remove('active');
                navOverlay.classList.remove('active');
                document.documentElement.classList.remove('block-viewport');
                document.body.classList.remove('block-viewport');
                navSticky.classList.remove('nav-fixed');
                closeIcon.classList.remove('active'); // Retirer la classe active ici aussi
            });
        }
    }

    // === CLOSE NAV RESPONSIVE (AUTRE PART) ===
    closeNavResponsive() {
        const navResponsive = document.querySelector('.nav-responsive');
        const navBurger = document.querySelector('.nav-burger');
        const closeIcon = document.querySelector('.nav-responsive-close_icon');
        const navOverlay = document.querySelector('.nav-responsive-overlay');

        if (navResponsive && navBurger && closeIcon && navOverlay) {
            navResponsive.classList.remove('active');
            navBurger.classList.remove('active');
            navOverlay.classList.remove('active');
            closeIcon.classList.remove('active'); // Assurez-vous de retirer la classe active
            this.manageViewportBlock(false);
        }
    }



    // === SWIPE NAV LISTENER ===
    initSwipeListeners() {
        const navResponsive = document.querySelector('.nav-responsive');
        const navCartModal = document.querySelector('.nav-cart-modal');
        if (navResponsive) {
            navResponsive.addEventListener('touchstart', (e) => this.handleTouchStart(e), false);
            navResponsive.addEventListener('touchmove', (e) => this.handleTouchMove(e, 'responsive'), false);
        }
        if (navCartModal) {
            navCartModal.addEventListener('touchstart', (e) => this.handleTouchStart(e), false);
            navCartModal.addEventListener('touchmove', (e) => this.handleTouchMove(e, 'cart'), false);
        }
    }

    handleTouchStart(e) {
        const firstTouch = e.touches[0];
        this.startX = firstTouch.clientX;
        this.startY = firstTouch.clientY;
    }

    handleTouchMove(e, type) {
        if (!this.startX || !this.startY) {
            return;
        }
        const moveEndX = e.touches[0].clientX;
        const moveEndY = e.touches[0].clientY;
        const diffX = this.startX - moveEndX;
        const diffY = this.startY - moveEndY;
        if (Math.abs(diffX) > Math.abs(diffY)) {
            if (diffX > 0 && type === 'responsive') {
                this.closeNavResponsive();
            } else if (diffX < 0 && type === 'cart') {
                this.toggleCart(false);
            }
        }
        this.startX = 0;
        this.startY = 0;
    }

    // === CLOSE NAV REPSONSIVE ===
    closeNavResponsive() {
        document.querySelector('.nav-responsive').classList.remove('active');
        document.querySelector('.nav-burger').classList.remove('active');
        this.manageViewportBlock(false);
    }

    // === BLOCKVIEWPORT ===
    manageViewportBlock(shouldBlock) {
        if (shouldBlock) {
            document.documentElement.classList.add("block-viewport");
            document.body.classList.add("block-viewport");
        } else {
            document.documentElement.classList.remove("block-viewport");
            document.body.classList.remove("block-viewport");
        }
    }

    // === RESPONSIVE SUB MENU ===
    initResponsiveSubMenus() {
        document.querySelectorAll(".nav-responsive ul.menu > li.menu-item-has-children > a").forEach(function(link) {
            link.addEventListener("click", function(e) {
                e.preventDefault();
                const parentLi = this.closest("li.menu-item-has-children");
                const subMenu = parentLi.querySelector("ul.sub-menu");
                const isActive = parentLi.classList.contains("active");

                document.querySelectorAll(".nav-responsive ul.menu li.menu-item-has-children").forEach(function(item) {
                    if (item !== parentLi) {
                        item.classList.remove("active");

                        const otherSubMenu = item.querySelector("ul.sub-menu");
                        if( otherSubMenu ) {
                            gsap.to(otherSubMenu, {
                                height: 0,
                                opacity: 0,
                                duration: .5,
                                ease: "power2.inOut",
                                onComplete: () => {
                                    otherSubMenu.style.display = "none";
                                }
                            });
                        }
                    }
                });
                if (!isActive) {
                    parentLi.classList.add("active");

                    subMenu.style.display = "flex";
                    const subMenuHeight = subMenu.scrollHeight;

                    gsap.fromTo(subMenu, {
                        height: 0,
                        opacity: 0
                    }, {
                        height: subMenuHeight,
                        opacity: 1,
                        duration: .5,
                        ease: "power2.inOut",
                        clearProps: 'height'
                    });
                } else {
                    parentLi.classList.remove("active");
                    gsap.to(subMenu, {
                        height: 0,
                        opacity: 0,
                        duration: .5,
                        ease: "power2.inOut",
                        onComplete: () => {
                            subMenu.style.display = "none";
                        }
                    });
                }
            });
        });
    }



    // === HREF HAVE ANCHOR ===
    attachAnchorListeners() {
        document.querySelectorAll('li.menu-item a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', function(e) {
                e.preventDefault();
                const targetId = this.getAttribute('href');
                if (targetId === '#' || !document.querySelector(targetId)) {
                    console.log("Ancre non valide ou ignorée : " + targetId);
                    return;
                }
                const targetElement = document.querySelector(targetId);
                if(targetElement) {
                    gsap.to(window, {
                        scrollTo: {
                            y: targetElement,
                            autoKill: false
                        },
                        duration: 1
                    });
                }
            });
        });
    }

    // === SCROLL TO ANCHOR ===
    scrollToAnchorOnLoad() {
        const hash = window.location.hash;
        if (hash) {
            const targetElement = document.querySelector(hash);
            if (targetElement) {
                gsap.to(window, {
                    scrollTo: {
                        y: targetElement,
                        autoKill: false
                    },
                    duration: 1,
                    delay: 0.25
                });
            }
        }
    }
}